import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const WAIT_LIMIT = 6000;
/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const frame = () => new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));

/** @param {Element | null} element */
const lostFocus = (element) =>
  !element ||
  element === document.body ||
  !element.isConnected ||
  !(element instanceof HTMLElement) ||
  element.offsetParent === null;

/**
 * Where focus goes after an upsell add: the new line's Remove button (a button, so no on-screen
 * keyboard on touch, and its label names the product), else the upsell heading, else the drawer heading.
 * @param {HTMLElement} items - the cart-items-component
 * @param {string} variantId
 * @returns {HTMLElement | null}
 */
const focusTarget = (items, variantId) => {
  const row = [...items.querySelectorAll('.cart-items__table-row[data-key]')].find((el) =>
    (el.getAttribute('data-key') ?? '').startsWith(`${variantId}:`)
  );
  const remove = row?.querySelector('.cart-items__remove');
  if (remove instanceof HTMLElement && remove.offsetParent !== null) return remove;
  const scope = items.closest('dialog') ?? items;
  const heading = scope.querySelector('.vb-cart-upsell__heading') ?? scope.querySelector('#cart-drawer-heading');
  if (!(heading instanceof HTMLElement)) return null;
  heading.setAttribute('tabindex', '-1');
  return heading;
};

/**
 * The drawer morphs after an add, which removes the pressed ADD button and drops focus to <body>.
 * Wait until Horizon has finished (list not busy, no row mid-insert), then keep watching briefly and
 * put focus on the new line whenever it has been lost. Same approach as assets/vb-cart-a11y.js.
 * @param {string} sectionId
 * @param {string} variantId
 */
const restoreFocus = async (sectionId, variantId) => {
  if (!sectionId) return;
  const find = () =>
    /** @type {HTMLElement | null} */ (
      document.querySelector(`cart-items-component[data-section-id="${CSS.escape(sectionId)}"]`)
    );
  const started = Date.now();
  await wait(60);
  while (Date.now() - started < WAIT_LIMIT) {
    const items = find();
    const ready =
      items &&
      !items.classList.contains('cart-items-disabled') &&
      !items.querySelector('.adding, .removing') &&
      items.querySelector(`.cart-items__table-row[data-key^="${CSS.escape(variantId)}:"]`);
    if (ready) break;
    await wait(80);
  }
  const until = Date.now() + 1500;
  while (Date.now() < until) {
    await frame();
    const items = find();
    if (items && lostFocus(document.activeElement)) focusTarget(items, variantId)?.focus();
    await wait(100);
  }
};

/**
 * <vb-cart-upsell>: the "You may also like" strip in the cart drawer (snippets/vb-cart-upsell.liquid).
 * - Prev/next buttons scroll the row by one view and are disabled at either end.
 * - "Add to cart" posts to /cart/add.js with the cart sections, then dispatches the same
 *   CartLinesUpdateEvent that Horizon's product form uses, so the drawer, the header count and
 *   the free shipping bar all re-render through Horizon's own listeners.
 * - After a successful add, focus moves to the new line's Remove button once the re-render is done
 *   (the pressed ADD button is gone by then), so keyboard and screen-reader users keep their place.
 */
class VbCartUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #frame = 0;
  #statusTimer = 0;

  /**
   * Looked up on every use: Horizon morphs the drawer in place after each cart change, so a
   * track reference kept from connectedCallback could point at a node that is gone.
   * @returns {HTMLElement | null}
   */
  get #track() {
    return this.querySelector('.vb-cart-upsell__track');
  }

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.addEventListener('click', this.#onClick, { signal });
    // scroll does not bubble; listening in the capture phase on the host survives a morphed track
    this.addEventListener('scroll', this.#queueArrows, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#queueArrows, { passive: true, signal });
    // The drawer's <dialog> is display:none until it opens, so the first measurement sees a
    // 0-wide track and disables both arrows. Re-measure when the strip gets its real size
    // (display:none -> visible fires the observer), after every cart re-render, and when a drawer opens.
    document.addEventListener('theme-drawer:open', this.#queueArrows, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#queueArrows);
      this.#resizeObserver.observe(this);
    }
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#statusTimer);
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #onCartUpdate = (event) => {
    const done = event.promise ?? Promise.resolve();
    // re-measure once the drawer has been re-rendered with the new lines
    done.then(() => setTimeout(this.#queueArrows, 50)).catch(() => {});
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const arrow = target.closest('.vb-cart-upsell__arrow');
    if (arrow instanceof HTMLButtonElement) {
      this.#scrollBy(Number(arrow.dataset.step) || 1);
      return;
    }
    const add = target.closest('button.vb-cart-upsell__add');
    if (add instanceof HTMLButtonElement && add.dataset.variantId) {
      this.#add(add);
    }
  };

  /** @param {number} step */
  #scrollBy(step) {
    const track = this.#track;
    if (!track) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: step * track.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' });
  }

  #queueArrows = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#updateArrows());
  };

  #updateArrows() {
    const track = this.#track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth - 1;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const pos = Math.abs(track.scrollLeft);
    for (const arrow of this.querySelectorAll('.vb-cart-upsell__arrow')) {
      if (!(arrow instanceof HTMLButtonElement)) continue;
      const step = Number(arrow.dataset.step) * (isRtl ? -1 : 1);
      arrow.disabled = step < 0 ? pos <= 1 : pos >= max;
    }
  }

  /** @param {string} key */
  #string(key) {
    const template = this.querySelector('template[data-vb-upsell-strings]');
    if (!(template instanceof HTMLTemplateElement)) return '';
    return template.content.querySelector(`[data-${key}]`)?.textContent?.trim() ?? '';
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('.vb-cart-upsell__status');
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
    clearTimeout(this.#statusTimer);
    this.#statusTimer = setTimeout(() => {
      const current = this.querySelector('.vb-cart-upsell__status');
      if (current) current.textContent = '';
    }, 4000);
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const routes = /** @type {any} */ (window).Theme?.routes ?? {};
    const addUrl = routes.cart_add_url || '/cart/add';
    const cartUrl = routes.cart_url || '/cart';

    try {
      const response = await fetch(addUrl.endsWith('.js') ? addUrl : `${addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await (await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();

      if (!response.ok || result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'vb-cart-upsell', itemCount: 1 },
        });
        this.#announce(result.description || this.#string('error'));
        button.removeAttribute('aria-busy');
        return;
      }

      this.#announce(this.#string('added'));
      const sectionId = this.closest('cart-items-component')?.getAttribute('data-section-id') ?? '';
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'vb-cart-upsell',
          itemCount: 1,
          sections: result.sections,
        },
      });
      restoreFocus(sectionId, variantId);
    } catch (error) {
      deferred.reject(error);
      this.#announce(this.#string('error'));
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('vb-cart-upsell')) customElements.define('vb-cart-upsell', VbCartUpsell);
